import React, { useCallback, useEffect, useMemo } from 'react';
import {
  useDispatch,
  useSelector,
  shallowEqual,
  batch,
} from 'react-redux';
import { bindActionCreators } from 'redux';
import cogoToast from 'cogo-toast';

import { Form, Input, FieldError } from '@common/ui/redux_form';
import { Field } from '@common/ui/form';

import { actions } from '~referral/stores/linkedinPostShare';
import { requestCallbackSelector } from '~referral/selectors/referralCallback';
import { REFERRAL_TRACKING } from '~referral/utils/trackingEvents';
import {
  setStreakReferralModal,
  setPostShareLinkedin,
  setReferralInfoModalState,
} from '~referral/stores/streakReferralModal';
import { Tappable } from '@common/ui/general';
import {
  updateProfileModalState,
} from '~profile_stats/stores/profileStatsModal';
import { validators } from '~referral/utils/linkedinFormValidator';
import greenCheckMark from '~referral/images/happy-flows/green-check.svg';

function CoinFormModal({
  popOverContent,
  analytics,
  onClose,
  nudgeType,
  hasUGAccess = false,
  isUgCgrStreakStats = false,
}) {
  const dispatch = useDispatch();

  const validator = validators.linkedinForm();

  const formActions = useMemo(() => bindActionCreators(actions, dispatch),
    [dispatch]);

  const formState = useSelector(
    state => state.linkedinPostShare,
    shallowEqual,
  );

  const validateForm = useCallback((updateErrors = true) => {
    const { updateFieldErrors } = formActions;
    const { fields } = formState;
    const errors = validator.check(fields, { validateOnlyIfPresent: false });
    if (errors && updateErrors) { updateFieldErrors(errors); }
    return !errors;
  }, [formActions, formState, validator]);
  const coinStates = {
    shared: `You'll be eligible to claim Scaler coins for only 
      2 LinkedIn posts in a month`,
    nonShared: 'We will credit your Scaler Coins upon verification.',
  };
  const coinStatesSst = {
    shared: `You'll be eligible to claim Scaler coins 
      for #SST-Revisited-24 only once`,
    nonShared: 'We will credit your Scaler Coins upon verification.',
  };
  const cgrAndStreakStats = {
    shared: `You will be eligible to claim the
   Scaler coins for this activity only once per trimester`,
    nonShared: 'We will credit your Scaler Coins upon verification.',
  };

  const {
    isLoading: processingRequest,
    coinSharedData,
  } = useSelector(requestCallbackSelector);

  const currentPagePath = window.location.pathname;

  const handleClose = useCallback(() => {
    batch(() => {
      dispatch(setReferralInfoModalState(true));
      dispatch(setStreakReferralModal(false));
      dispatch(updateProfileModalState(false));
      onClose?.();
    });
  }, [dispatch, onClose]);

  useEffect(() => {
    dispatch(setStreakReferralModal(true));
  }, [dispatch]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (validateForm()) {
      batch(() => {
        handleClose();
        formActions.updateField('location', nudgeType);
        dispatch(setPostShareLinkedin(false));
        analytics?.click(
          REFERRAL_TRACKING.clickReferralCallbackRequest,
          'Nudge Happy flow Shared Modal', {
            pageSource: currentPagePath,
          },
        );
      });
      try {
        await formActions.submitForm();
        cogoToast.success('Successful Submit');
      } catch (e) {
        cogoToast.error(
          `Unsuccessful submit,recheck the
            description field, fill atleast one!`,
        );
      }
    } else {
      cogoToast.error('Linkedin URL link is wrong');
    }
  }, [validateForm,
    dispatch, handleClose, formActions, nudgeType, analytics, currentPagePath]);

  return (
    <div className="">
      <Form
        formActions={formActions}
        formState={formState}
        onSubmit={handleSubmit}
      >
        <div className="nudge-shared-modal__content">
          <div className="nudge-shared-modal__list-container">
            <div className="nudge-shared-modal__list-item
              nudge-shared-modal__list-highlight"
            >
              <img
                className="nudge-shared-modal__list-emoji"
                src={greenCheckMark}
                alt="check-icon"
              />
              {popOverContent?.first}
            </div>
            <ul className="nudge-shared-modal__list">
              <li className="nudge-shared-modal__li">
                <div className="nudge-shared-modal__li--number">2</div>
                <span>{popOverContent?.second}</span>
              </li>
              <li className="nudge-shared-modal__li">
                <div className="nudge-shared-modal__li--number">3</div>
                <div className="column flex-fill">
                  <span
                    className="nudge-shared-modal__field-name"
                  >
                    {popOverContent?.third}
                  </span>
                  <Field
                    error={<FieldError name="post_link" className="danger" />}
                    required
                    key="linkedin_link"
                    className=""
                  >
                    <Input
                      name="post_link"
                      type="text"
                      placeholder="Linkedin link"
                    />
                  </Field>
                </div>
              </li>
              <div className="nudge-shared-modal__button-container">
                <div className="nudge-shared-modal__li--number">4</div>
                <Tappable
                  className="btn btn-primary nudge-shared-modal__li--btn"
                  component="button"
                  disabled={
                    processingRequest || formState.submitting
                        || formState.fields.post_link?.length === 0
                  }
                  type="submit"
                >
                  Submit Here
                </Tappable>
                {coinSharedData && (
                  <div className="nudge-shared-modal__coin-status m-l-10">
                    {
                      isUgCgrStreakStats && cgrAndStreakStats.nonShared
                    }
                    {
                      !isUgCgrStreakStats
                      && hasUGAccess ? (
                          coinStatesSst.nonShared) : (coinStates.nonShared
                        )
                    }
                  </div>
                )}
              </div>
            </ul>
            <div className="nudge-shared-modal__coin-note m-t-10">
              <strong>Note: </strong>
              {
                isUgCgrStreakStats && cgrAndStreakStats.shared
              }
              {
                !isUgCgrStreakStats
                  && (hasUGAccess ? (
                    coinStatesSst.shared) : (coinStates.shared
                  ))
              }
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
}

export default CoinFormModal;
